import { useEffect, useMemo, useRef, useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Link, useParams } from 'react-router-dom';
import PurchaseOrderAiReviewButton from '@v2/features/ai/PurchaseOrderAiReviewButton';
import PoColorCheckPanel from '@v2/features/purchase-order/PoColorCheckPanel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowUpBold,
    PiArrowSquareOutBold,
    PiArrowsClockwiseBold,
    PiBuildingOfficeBold,
    PiCaretDownBold,
    PiCheckCircleBold,
    PiCheckBold,
    PiCubeBold,
    PiDotsThreeBold,
    PiFunnelBold,
    PiPencilSimpleBold,
    PiFilePdfBold,
    PiSpinnerBold,
    PiFileTextBold,
    PiPackageBold,
    PiLinkBold,
    PiLockKeyOpenBold,
    PiMagnifyingGlassBold,
    PiPaperPlaneTiltBold,
    PiPlusBold,
    PiShoppingCartSimpleBold,
    PiTrashBold,
    PiTruckBold,
    PiWarningBold,
    PiXBold,
} from 'react-icons/pi';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import V2Modal from '@v2/components/ui/V2Modal';
import PoEmailPreviewModal from '@v2/features/purchase-order/PoEmailPreviewModal';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { type ColorHexMaps, type ColorSlot } from '@v2/features/colors/colorHex';
import {
    COLOR_SLOTS,
    COLOR_SLOT_LABELS as COLOR_LABELS,
    ColorDot,
    ColorLegend,
    DOT_STYLE,
    dotInlineStyle,
    dotState,
} from '@v2/features/colors/ColorDot';
import { ContainerSlot } from '@v2/features/containers/ContainerSlot';
import { fadeZoomIn, flash, pop, useCountUp, useStaggerIn } from '@v2/lib/motion';

/* ============================== TYPES ============================== */

type POState = {
    shipment: { id: number; order_number: string | null; contract_type: string | null };
    po: {
        id: number;
        code: string;
        status: 'draft' | 'closed';
        color: string;
        notes: string | null;
        supplier: string | null;
        supplier_name: string | null;
        supplier_label: string;
        import_cnpj: string | null;
        import_cnpj_label: string;
        is_setup_complete: boolean;
        is_closed: boolean;
        closed_at: string | null;
        pdf_url: string | null;
        pdf_preview_url: string;
        pdfs: { lang: string; url: string }[];
        email_sent_at: string | null;
        email_sent_to: string | null;
        extra_order_ids: number[];
    };
    items: POItem[];
    totals: { usd: number; cbm: number; containers: number; cbm_per_container: number; max_containers: number | null };
    available_orders: AvailableOrder[];
    orphaned_orders: OrphanedOrder[];
    suppliers: { slug: string; name: string; legal_name?: string | null; product_supplier_code: string | null; max_containers?: number | null }[];
    branches: { slug: string; name: string; legal_name: string | null; cnpj?: string | null; state_registration?: string | null }[];
    supplier_detail: SupplierDetail | null;
    branch_detail: BranchDetail | null;
    color_hex_maps: ColorHexMaps;
    edit_v1_url: string;
};

type POItem = {
    ploomes_order_product_id: number;
    ploomes_order_id: number;
    product_id: number | null;
    product_code: string | null;
    product_name: string;
    quantity: number;
    unit_price_usd: number;
    discount_percent: number;
    cbm_unit: number;
    frame_colour: string | null;
    colour_upholstery: string | null;
    stitches: string | null;
    arm_colour: string | null;
};

type AvailableProduct = {
    is_high_turnover?: boolean;
    ploomes_order_product_id: number;
    product_code: string | null;
    product_name: string;
    quantity_total: number;
    quantity_available: number;
    quantity_in_po?: number;
    unit_price_usd: number;
    discount_percent: number;
    cbm_unit: number;
    frame_colour: string | null;
    colour_upholstery: string | null;
    stitches: string | null;
    arm_colour: string | null;
    image_url: string | null;
    product_id: number | null;
    order_number: string | null;
};

type AvailableOrder = {
    ploomes_order_id: number;
    order_number: string | null;
    order_date: string | null;
    products: AvailableProduct[];
};

type OrphanedOrder = {
    ploomes_order_id: number;
    order_number: string | null;
    order_date: string | null;
    items_count: number;
};

type SupplierDetail = {
    slug: string;
    name: string;
    legal_name: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
    attn: string | null;
    max_containers: number | null;
    product_supplier_code: string | null;
    manage_url: string;
};

type BranchDetail = {
    slug: string;
    name: string;
    legal_name: string | null;
    cnpj: string | null;
    state_registration: string | null;
    address_line: string | null;
    district: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    contact_person: string | null;
    full_address: string | null;
    manage_url: string;
};

type OrderSearchResult = {
    id: number;
    order_number: string | null;
    date: string | null;
    products_count: number;
    total_usd: number | null;
    deal_id: number | null;
};

/* ============================== HELPERS ============================== */

const fmtUSD = (n: number) =>
    '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCBM = (n: number, d = 4) =>
    n.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (n: number) =>
    '−' + n.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + '%';
const fmtQty = (n: number) => {
    const s = n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
    return s;
};

const SECONDARY_BTN =
    'inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60';

type SlotColors = Pick<AvailableProduct, 'frame_colour' | 'colour_upholstery' | 'stitches' | 'arm_colour'>;

const slotColor = (p: SlotColors, slot: ColorSlot): string | null =>
    slot === 'frame'
        ? p.frame_colour
        : slot === 'upholstery'
          ? p.colour_upholstery
          : slot === 'stitches'
            ? p.stitches
            : p.arm_colour;

/* ============================== PAGE ============================== */

export default function ShipmentPurchaseOrderPage() {
    const { id } = useParams<{ id: string }>();
    const qc = useQueryClient();
    const queryKey = ['shipment-po-editor', id];

    const { data, isLoading, error } = useQuery({
        queryKey,
        queryFn: async () => (await api.get<POState>(`/shipments/${id}/purchase-order/editor`)).data,
    });

    const onRefresh = (next: POState) => qc.setQueryData(queryKey, next);

    if (isLoading) return <LoadingState />;
    if (error) return <p className="p-6 text-sm text-red-600">Erro ao carregar Purchase Order.</p>;
    if (!data) return null;

    return <PurchaseOrderEditor state={data} shipmentId={id!} onRefresh={onRefresh} />;
}

/* ============================== EDITOR ============================== */

function PurchaseOrderEditor({
    state,
    shipmentId,
    onRefresh,
}: {
    state: POState;
    shipmentId: string;
    onRefresh: (next: POState) => void;
}) {
    const { po, items, totals, available_orders, shipment, supplier_detail, branch_detail, color_hex_maps } = state;
    const closed = po.is_closed;
    const needsSetup = !po.is_setup_complete;
    const totalQty = items.reduce((s, i) => s + i.quantity, 0);
    const supplierLogoMap: Record<string, string> = {
        real_leader: '/img/logo_real_leader.webp',
        okpro: '/img/OKPRO_LOGO.png',
        spirit: '/img/spirit_logo.png',
    };
    const supplierLogoUrl = supplier_detail?.slug ? (supplierLogoMap[supplier_detail.slug] ?? null) : null;
    const [showSetup, setShowSetup] = useState(needsSetup);
    const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
    const [confirmState, setConfirmState] = useState<null | { type: 'close' | 'clear' | 'reopen' }> (null);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);

    const syncColorsMut = useMutation({
        mutationFn: async () => {
            const { data } = await api.post(`/shipments/${shipment.id}/purchase-order/editor/sync-colors`);
            return data;
        },
        onSuccess: (data) => {
            // O endpoint retorna o estado completo atualizado — atualiza o cache direto
            const { message, ...nextState } = data;
            if (Object.keys(nextState).length > 0) {
                onRefresh(nextState as POState);
            }
            setToast({ kind: 'success', msg: message ?? 'Cores atualizadas!' });
        },
        onError: (e: any) => {
            const msg = e?.response?.data?.message ?? e?.message ?? 'Erro ao sincronizar cores.';
            setToast({ kind: 'error', msg });
        },
    });

    const playAlert = () => {
        try {
            const audio = new Audio('/sound/alert.mp3');
            audio.volume = 0.6;
            audio.play().catch(() => {});
        } catch {}
    };

    const handlePreviewPdf = async () => {
        setGeneratingPdf(true);
        setToast(null);
        try {
            const res = await fetch(po.pdf_preview_url, { credentials: 'same-origin' });
            if (!res.ok) {
                const detail = (await res.text()).trim().slice(0, 240);
                throw new Error(detail || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            playAlert();
            setToast({ kind: 'success', msg: 'PDF gerado com sucesso — abrindo em nova aba.' });
        } catch (e) {
            const msg = e instanceof Error && e.message ? e.message : 'Erro ao gerar o PDF. Tente novamente.';
            setToast({ kind: 'error', msg });
        } finally {
            setGeneratingPdf(false);
        }
    };

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(t);
    }, [toast]);

    const callRefresh = (data: any) => {
        const next = data?.state ?? data;
        if (next?.po) onRefresh(next as POState);
    };

    const closeMut = useMutation({
        mutationFn: () => api.post(`/shipments/${shipmentId}/purchase-order/editor/close`).then((r) => r.data),
        onSuccess: (d) => {
            callRefresh(d);
            playAlert();
            setToast({ kind: 'success', msg: 'PO fechada e PDF gerado.' });
        },
        onError: (e: any) => setToast({ kind: 'error', msg: e?.response?.data?.message ?? 'Erro ao fechar PO.' }),
    });

    const reopenMut = useMutation({
        mutationFn: () => api.post(`/shipments/${shipmentId}/purchase-order/editor/reopen`).then((r) => r.data),
        onSuccess: callRefresh,
    });

    const clearMut = useMutation({
        mutationFn: () => api.post(`/shipments/${shipmentId}/purchase-order/editor/clear`).then((r) => r.data),
        onSuccess: (d) => {
            callRefresh(d);
            setToast({ kind: 'success', msg: 'Todos os itens removidos.' });
        },
    });

    const sendEmailMut = useMutation({
        mutationFn: () => api.post(`/shipments/${shipmentId}/purchase-order/editor/send-email`).then((r) => r.data),
        onSuccess: (d) => {
            callRefresh(d);
            setEmailModalOpen(false);
            setToast({ kind: 'success', msg: 'E-mail enviado ao fornecedor.' });
        },
        onError: (e: any) => setToast({ kind: 'error', msg: e?.response?.data?.message ?? 'Erro ao enviar e-mail.' }),
    });

    // quantidade adicionada por OOP (drives o badge "X/Y disp." e o "X na PO")
    const usedByOop = useMemo(() => {
        const m = new Map<number, number>();
        items.forEach((i) => m.set(i.ploomes_order_product_id, (m.get(i.ploomes_order_product_id) ?? 0) + i.quantity));
        return m;
    }, [items]);

    // quantidade de itens (linhas) por order — para mostrar "X na PO"
    const itemsByOrder = useMemo(() => {
        const m = new Map<number, number>();
        items.forEach((i) => m.set(i.ploomes_order_id, (m.get(i.ploomes_order_id) ?? 0) + 1));
        return m;
    }, [items]);

    // pedidos únicos na PO com seus order_numbers
    const ordersInPo = useMemo(() => {
        const numberMap = new Map<number, string | null>();
        [...state.available_orders, ...state.orphaned_orders].forEach((o) =>
            numberMap.set(o.ploomes_order_id, o.order_number),
        );
        // IDs que têm pelo menos 1 item na PO
        const addedIds = new Set(items.map((i) => i.ploomes_order_id));

        // Todos os pedidos do embarque (adicionados + disponíveis)
        const allIds = new Set([
            ...addedIds,
            ...state.available_orders.map((o) => o.ploomes_order_id),
            ...state.orphaned_orders.map((o) => o.ploomes_order_id),
        ]);

        return [...allIds].map((id) => ({
            id,
            label: numberMap.get(id) ?? `#${id}`,
            added: addedIds.has(id),
        })).sort((a, b) => {
            const na = parseInt(String(a.label).replace(/\D/g, ''), 10);
            const nb = parseInt(String(b.label).replace(/\D/g, ''), 10);
            if (!isNaN(na) && !isNaN(nb)) return na - nb;
            return String(a.label).localeCompare(String(b.label));
        });
    }, [items, state.available_orders, state.orphaned_orders]);

    return (
        <div className="space-y-6">
            {/* Breadcrumb + título + ações topo */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <nav className="text-xs text-gray-500">
                        <Link to="/shipments" className="hover:underline">Embarques</Link>
                        <span className="mx-1">&gt;</span>
                        <Link to={`/shipments/${shipment.id}`} className="hover:underline">
                            {shipment.order_number ?? `#${shipment.id}`}
                        </Link>
                    </nav>
                    <h1 className="mt-1 text-2xl font-bold text-gray-800">
                        Purchase Order — <span>{po.code}</span>
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Link
                        to={`/shipments/${shipment.id}/pi-check`}
                        className={SECONDARY_BTN}
                    >
                        <PiCheckCircleBold className="size-4 text-emerald-600" /> Validar PI
                    </Link>
                    {items.length > 0 && <PurchaseOrderAiReviewButton shipmentId={shipment.id} />}
                    {!closed && (
                        <button
                            type="button"
                            onClick={() => syncColorsMut.mutate()}
                            disabled={syncColorsMut.isPending}
                            title="Busca as cores dos pedidos diretamente do Ploomes"
                            className={SECONDARY_BTN}
                        >
                            {syncColorsMut.isPending
                                ? <PiSpinnerBold className="size-4 animate-spin text-blue-600" />
                                : <PiArrowsClockwiseBold className="size-4 text-blue-600" />}
                            {syncColorsMut.isPending ? 'Sincronizando…' : 'Sincronizar cores'}
                        </button>
                    )}
                    {!closed && items.length > 0 && po.is_setup_complete && (
                        <button
                            type="button"
                            onClick={handlePreviewPdf}
                            disabled={generatingPdf}
                            className={SECONDARY_BTN}
                        >
                            {generatingPdf
                                ? <PiSpinnerBold className="size-4 animate-spin" />
                                : <PiFilePdfBold className="size-4 text-gray-500" />}
                            {generatingPdf ? 'Gerando PDF…' : 'Pré-visualizar PDF'}
                        </button>
                    )}
                    {!closed && (
                        // Ações raras ou destrutivas fora da linha principal: evita clique errado ao lado de "Fechar".
                        <Menu as="div" className="relative">
                            <MenuButton className={SECONDARY_BTN + ' px-2.5'} title="Mais ações" aria-label="Mais ações">
                                <PiDotsThreeBold className="size-5" />
                            </MenuButton>
                            <MenuItems
                                anchor="bottom end"
                                className="z-[60] mt-1 w-60 origin-top-right rounded-xl border border-gray-200 bg-white p-1 shadow-lg focus:outline-none"
                            >
                                <MenuItem>
                                    <button
                                        type="button"
                                        onClick={() => setShowSetup(true)}
                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100"
                                    >
                                        <PiPencilSimpleBold className="size-4 text-gray-500" /> Alterar fornecedor / CNPJ
                                    </button>
                                </MenuItem>
                                {items.length > 0 && (
                                    <MenuItem>
                                        <button
                                            type="button"
                                            onClick={() => setConfirmState({ type: 'clear' })}
                                            disabled={clearMut.isPending}
                                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 data-[focus]:bg-red-50 disabled:opacity-60"
                                        >
                                            <PiTrashBold className="size-4" /> Apagar PO (remover todos os itens)
                                        </button>
                                    </MenuItem>
                                )}
                            </MenuItems>
                        </Menu>
                    )}
                    {!closed ? (
                        <button
                            type="button"
                            onClick={() => setConfirmState({ type: 'close' })}
                            disabled={closeMut.isPending || items.length === 0 || !po.is_setup_complete}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 hover:shadow disabled:opacity-50"
                            title={
                                closeMut.isPending
                                    ? 'Processando…'
                                    : items.length === 0
                                        ? 'Adicione pelo menos 1 item à PO.'
                                        : !po.is_setup_complete
                                            ? 'Complete o setup do fornecedor/CNPJ.'
                                            : ''
                            }
                        >
                            <PiCheckCircleBold /> {closeMut.isPending ? 'Processando…' : 'Fechar e enviar PO'}
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => setConfirmState({ type: 'reopen' })}
                                disabled={reopenMut.isPending}
                                className={SECONDARY_BTN}
                            >
                                <PiLockKeyOpenBold className="size-4 text-amber-600" /> Reabrir
                            </button>
                            <button
                                type="button"
                                onClick={() => setEmailModalOpen(true)}
                                disabled={sendEmailMut.isPending}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 hover:shadow disabled:opacity-60"
                            >
                                <PiPaperPlaneTiltBold /> {po.email_sent_at ? 'Reenviar e-mail' : 'Enviar e-mail'}
                            </button>
                        </>
                    )}
                                {/* Modais de confirmação */}
                                <ConfirmModal
                                    open={!!confirmState && confirmState.type === 'close'}
                                    onClose={() => setConfirmState(null)}
                                    onConfirm={() => {
                                        setConfirmState(null);
                                        closeMut.mutate();
                                    }}
                                    title="Fechar a PO e gerar PDF?"
                                    description="Após isso ela ficará bloqueada para edição."
                                    confirmLabel={closeMut.isPending ? 'Processando…' : 'Fechar e enviar PO'}
                                    loading={closeMut.isPending}
                                />
                                <ConfirmModal
                                    open={!!confirmState && confirmState.type === 'clear'}
                                    onClose={() => setConfirmState(null)}
                                    onConfirm={() => {
                                        setConfirmState(null);
                                        clearMut.mutate();
                                    }}
                                    title="Remover TODOS os itens desta Purchase Order?"
                                    description="Esta ação não pode ser desfeita."
                                    confirmLabel={clearMut.isPending ? 'Removendo…' : 'Remover todos'}
                                    loading={clearMut.isPending}
                                />
                                <PoEmailPreviewModal
                                    open={emailModalOpen}
                                    shipmentId={shipmentId}
                                    isResend={Boolean(po.email_sent_at)}
                                    sending={sendEmailMut.isPending}
                                    onClose={() => setEmailModalOpen(false)}
                                    onSend={() => sendEmailMut.mutate()}
                                />
                                <ConfirmModal
                                    open={!!confirmState && confirmState.type === 'reopen'}
                                    onClose={() => setConfirmState(null)}
                                    onConfirm={() => {
                                        setConfirmState(null);
                                        reopenMut.mutate();
                                    }}
                                    title="Reabrir PO para edição?"
                                    description="Você poderá editar novamente a PO."
                                    confirmLabel={reopenMut.isPending ? 'Reabrindo…' : 'Reabrir'}
                                    loading={reopenMut.isPending}
                                />
                </div>
            </div>

            {toast && (
                <div
                    className={`rounded-lg border px-4 py-2 text-sm ${
                        toast.kind === 'success'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-red-200 bg-red-50 text-red-700'
                    }`}
                >
                    {toast.msg}
                </div>
            )}

            {/* SETUP */}
            {showSetup && (
                <SetupCard
                    state={state}
                    shipmentId={shipmentId}
                    onRefresh={(next) => {
                        onRefresh(next);
                        if (next.po.is_setup_complete) setShowSetup(false);
                    }}
                    onCancel={() => po.is_setup_complete && setShowSetup(false)}
                />
            )}

            {items.length > 0 && (
                <PoColorCheckPanel
                    shipmentId={Number(shipmentId)}
                    closed={closed}
                    hexMaps={color_hex_maps}
                    signature={items.map((i) => [i.ploomes_order_product_id, i.quantity, i.frame_colour, i.colour_upholstery, i.stitches, i.arm_colour].join('~')).join('|')}
                    onFixed={() => {
                        void api.get<POState>(`/shipments/${shipmentId}/purchase-order/editor`).then((r) => onRefresh(r.data));
                    }}
                />
            )}

            {/* GRID PRINCIPAL */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                {/* === COLUNA ESQUERDA === */}
                <div className="space-y-6 xl:col-span-2">
                    {/* Header gradient da PO */}
                    <div className="overflow-hidden rounded-xl shadow-sm ring-1 ring-gray-200">
                        <div
                            className="px-6 py-5 text-white"
                            style={{ background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)' }}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex items-start gap-4">
                                    {supplierLogoUrl && (
                                        <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-xl p-2 ring-1 ring-white/20" style={{ background: 'rgba(255,255,255,0.08)' }}>
                                            <img
                                                src={supplierLogoUrl}
                                                alt={supplier_detail?.name ?? ''}
                                                className="max-h-full max-w-full object-contain drop-shadow-sm"
                                            />
                                        </div>
                                    )}
                                <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] opacity-80">
                                        Purchase Order
                                    </div>
                                    <div className="mt-1 text-3xl font-bold">{po.code}</div>
                                    <div className="mt-1 text-sm opacity-90">
                                        Embarque {shipment.order_number ?? `#${shipment.id}`}
                                    </div>
                                    {po.supplier_label && (
                                        <div className="mt-2">
                                            <span className="inline-flex items-center rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold tracking-wide text-white ring-1 ring-white/30">
                                                {po.supplier_label}
                                            </span>
                                        </div>
                                    )}
                                    {ordersInPo.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {ordersInPo.map((o) => (
                                                <button
                                                    key={o.id}
                                                    type="button"
                                                    onClick={() => {
                                                        document
                                                            .getElementById(`po-order-${o.id}`)
                                                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                    }}
                                                    className={
                                                        o.added
                                                            ? 'inline-flex items-center rounded-full bg-emerald-500/30 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-200 ring-1 ring-emerald-400/40 cursor-pointer hover:bg-emerald-500/45 transition-colors'
                                                            : 'inline-flex items-center rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-white/50 ring-1 ring-white/15 cursor-pointer hover:bg-white/20 transition-colors'
                                                    }
                                                >
                                                    {o.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                </div>
                                <div className="text-right">
                                    <span
                                        className={
                                            closed
                                                ? 'inline-flex rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-bold backdrop-blur'
                                                : 'inline-flex rounded-full bg-white/30 px-3 py-1 text-[11px] font-bold backdrop-blur'
                                        }
                                    >
                                        {closed ? '✓ FECHADA' : 'EM EDIÇÃO'}
                                    </span>
                                    <HeroTotals
                                        usd={totals.usd}
                                        cbm={totals.cbm}
                                        skus={items.length}
                                        qty={totalQty}
                                        containers={totals.containers}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pedidos disponíveis */}
                    <AvailableOrdersPanel
                        state={state}
                        shipmentId={shipmentId}
                        onRefresh={onRefresh}
                        usedByOop={usedByOop}
                        itemsByOrder={itemsByOrder}
                        onToast={setToast}
                        disabled={closed || !po.is_setup_complete}
                    />
                </div>

                {/* === COLUNA DIREITA === */}
                <div className="space-y-4">
                    <ContainersCard totals={totals} />
                    <SupplierCard detail={supplier_detail} />
                    <ImporterCard detail={branch_detail} />
                    <NotesCard state={state} shipmentId={shipmentId} onRefresh={onRefresh} disabled={closed} />
                    {closed && po.pdf_url && (
                        <PdfGeneratedCard pdfUrl={po.pdf_url} closedAt={po.closed_at} />
                    )}
                </div>
            </div>

            <BackToTopButton />
        </div>
    );
}

/* ============================== HERO TOTALS ============================== */

function HeroTotals({
    usd,
    cbm,
    skus,
    qty,
    containers,
}: {
    usd: number;
    cbm: number;
    skus: number;
    qty: number;
    containers: number;
}) {
    const totalRef = useRef<HTMLDivElement>(null);
    const prevUsd = useRef(usd);
    const shownUsd = useCountUp(usd);
    const shownCbm = useCountUp(cbm);

    useEffect(() => {
        if (prevUsd.current !== usd) pop(totalRef.current);
        prevUsd.current = usd;
    }, [usd]);

    return (
        <>
            <div ref={totalRef} className="mt-2 origin-right text-2xl font-bold tabular-nums">
                {fmtUSD(shownUsd)}
            </div>
            <div className="text-xs tabular-nums opacity-90">
                {skus > 0 && <>{skus} SKUs · </>}
                {qty > 0 && <>{qty} equip. · </>}
                {fmtCBM(shownCbm)} m³ · {containers} cont.
            </div>
        </>
    );
}

/* ============================== BACK TO TOP ============================== */

function BackToTopButton() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 300);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    if (!visible) return null;

    return (
        <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 z-50 flex size-10 items-center justify-center rounded-full bg-gray-800 text-white shadow-lg hover:bg-gray-700 transition-colors"
            title="Voltar ao topo"
        >
            <PiArrowUpBold className="size-4" />
        </button>
    );
}

/* ============================== SETUP CARD ============================== */

function SetupCard({
    state,
    shipmentId,
    onRefresh,
    onCancel,
}: {
    state: POState;
    shipmentId: string;
    onRefresh: (next: POState) => void;
    onCancel: () => void;
}) {
    const { po, suppliers, branches } = state;
    const [supplier, setSupplier] = useState(po.supplier ?? '');
    const [supplierName, setSupplierName] = useState(po.supplier_name ?? '');
    const [importCnpj, setImportCnpj] = useState(po.import_cnpj ?? '');

    const saveMut = useMutation({
        mutationFn: async () => {
            const { data } = await api.put(`/shipments/${shipmentId}/purchase-order/editor/setup`, {
                supplier,
                supplier_name: supplier === 'other' ? supplierName : null,
                import_cnpj: importCnpj,
            });
            return data;
        },
        onSuccess: (d) => onRefresh(d.state ?? d),
    });

    return (
        <div
            className="rounded-xl border border-amber-200 bg-white p-6 shadow-sm"
            style={{ borderTop: '4px solid #f59e0b' }}
        >
            <h2 className="text-lg font-bold text-gray-800">Configurar Purchase Order</h2>
            <p className="mt-1 text-sm text-gray-500">
                Selecione o fornecedor e a filial Buckler que receberá a importação.
            </p>

            <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Fornecedores */}
                <div>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Fornecedor</h3>
                    <div className="space-y-2">
                        {suppliers.map((s) => (
                            <label
                                key={s.slug}
                                className={
                                    'flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-gray-50 ' +
                                    (supplier === s.slug
                                        ? 'border-primary bg-primary-lighter/20'
                                        : 'border-gray-200')
                                }
                            >
                                <input
                                    type="radio"
                                    name="supplier"
                                    checked={supplier === s.slug}
                                    onChange={() => setSupplier(s.slug)}
                                    className="mt-1"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-gray-800">{s.name}</div>
                                    {s.legal_name && (
                                        <div className="text-xs text-gray-500">{s.legal_name}</div>
                                    )}
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {s.product_supplier_code && (
                                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-700">
                                                {s.product_supplier_code}
                                            </span>
                                        )}
                                        {s.max_containers ? (
                                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
                                                máx {s.max_containers} cont.
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            </label>
                        ))}
                        {supplier === 'other' && (
                            <input
                                type="text"
                                value={supplierName}
                                onChange={(e) => setSupplierName(e.target.value)}
                                placeholder="Nome do fornecedor"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            />
                        )}
                    </div>
                </div>

                {/* Filiais */}
                <div>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Importador (Filial Buckler)
                    </h3>
                    <div className="space-y-2">
                        {branches.map((b) => (
                            <label
                                key={b.slug}
                                className={
                                    'flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-gray-50 ' +
                                    (importCnpj === b.slug
                                        ? 'border-primary bg-primary-lighter/20'
                                        : 'border-gray-200')
                                }
                            >
                                <input
                                    type="radio"
                                    name="importCnpj"
                                    checked={importCnpj === b.slug}
                                    onChange={() => setImportCnpj(b.slug)}
                                    className="mt-1"
                                />
                                <div className="flex-1">
                                    <div className="font-semibold text-gray-800">{b.name}</div>
                                    {b.legal_name && <div className="text-xs text-gray-600">{b.legal_name}</div>}
                                    {b.cnpj && (
                                        <div className="text-xs text-gray-500">CNPJ: {b.cnpj}</div>
                                    )}
                                    {b.state_registration && (
                                        <div className="text-xs text-gray-500">IE: {b.state_registration}</div>
                                    )}
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => saveMut.mutate()}
                    disabled={
                        !supplier ||
                        !importCnpj ||
                        (supplier === 'other' && !supplierName.trim()) ||
                        saveMut.isPending
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-50"
                >
                    <PiCheckCircleBold /> {saveMut.isPending ? 'Salvando…' : 'Salvar configuração'}
                </button>
                {po.is_setup_complete && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                    >
                        Cancelar
                    </button>
                )}
            </div>
            {saveMut.isError && (
                <p className="mt-2 text-sm text-red-600">
                    {(saveMut.error as any)?.response?.data?.message ?? 'Erro ao salvar configuração.'}
                </p>
            )}
        </div>
    );
}

/* ============================== PRODUCT STEPPER ============================== */

function ProductStepper({
    product,
    orderId,
    used,
    available,
    total,
    disabled,
    onAdd,
}: {
    product: AvailableProduct;
    orderId: number;
    used: number;
    available: number;
    total: number;
    disabled: boolean;
    onAdd: (qty: number) => void;
}) {
    const [qty, setQty] = useState(1);
    const exhausted = available <= 0;

    const clamp = (v: number) => Math.max(1, Math.min(v, available));

    const handleChange = (v: number) => setQty(isNaN(v) ? 1 : clamp(v));

    return (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={() => handleChange(qty - 1)}
                disabled={disabled || exhausted || qty <= 1}
                className="flex size-7 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
            >
                −
            </button>
            <input
                type="number"
                min={1}
                max={available}
                value={qty}
                onChange={(e) => handleChange(parseInt(e.target.value, 10))}
                disabled={disabled || exhausted}
                className="w-12 rounded-md border border-gray-300 px-1 py-0.5 text-center text-sm font-semibold text-gray-800 disabled:opacity-40"
            />
            <button
                type="button"
                onClick={() => handleChange(qty + 1)}
                disabled={disabled || exhausted || qty >= available}
                className="flex size-7 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
            >
                +
            </button>
            <button
                type="button"
                onClick={() => { onAdd(qty); setQty(1); }}
                disabled={disabled || exhausted}
                title="Adicionar à PO"
                className="rounded-lg bg-primary/10 p-2 text-primary hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-30"
            >
                <PiPlusBold className="size-4" />
            </button>
        </div>
    );
}

/* ============================== AVAILABLE ORDERS PANEL ============================== */

function QtyStatus({ used, total, available }: { used: number; total: number; available: number }) {
    const ref = useRef<HTMLDivElement>(null);
    const prev = useRef(used);

    useEffect(() => {
        if (prev.current !== used) pop(ref.current);
        prev.current = used;
    }, [used]);

    const done = available <= 0 && used > 0;
    const partial = used > 0 && available > 0;

    return (
        <div className="w-24 flex-shrink-0 text-center">
            <div
                ref={ref}
                className={
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ' +
                    (done
                        ? 'bg-emerald-100 text-emerald-700'
                        : partial
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600')
                }
            >
                {done && <PiCheckBold className="size-3" />}
                {done ? `${fmtQty(used)} na PO` : partial ? `${fmtQty(used)}/${fmtQty(total)} na PO` : `${fmtQty(total)} disp.`}
            </div>
            {partial && <div className="mt-0.5 text-[10px] tabular-nums text-gray-500">faltam {fmtQty(available)}</div>}
        </div>
    );
}

function ProductColorsPanel({ colors, hexMaps }: { colors: SlotColors; hexMaps: ColorHexMaps }) {
    const listRef = useRef<HTMLUListElement>(null);
    useStaggerIn(listRef, COLOR_SLOTS.map((slot) => slotColor(colors, slot) ?? '').join('|'));

    return (
        <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Cores do pedido
            </h3>
            <ul ref={listRef} className="space-y-2">
                {COLOR_SLOTS.map((slot) => {
                    const name = slotColor(colors, slot);
                    const { state, hex } = dotState(slot, name, hexMaps);
                    const none = state === 'none';
                    return (
                        <li
                            key={slot}
                            data-motion-item
                            className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-gray-700/60 dark:bg-gray-800"
                        >
                            <span
                                className={
                                    'size-10 flex-shrink-0 rounded-full ' +
                                    (state === 'standard'
                                        ? 'shadow-inner ring-2 ring-white ring-offset-1 ring-offset-gray-200 dark:ring-gray-800 dark:ring-offset-gray-600'
                                        : DOT_STYLE[state])
                                }
                                style={dotInlineStyle(state, hex)}
                                aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    {COLOR_LABELS[slot]}
                                </p>
                                {none ? (
                                    <p className="text-sm text-gray-400">Sem customização</p>
                                ) : (
                                    <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100" title={name ?? ''}>
                                        {name}
                                    </p>
                                )}
                            </div>
                            {state === 'off' && (
                                <span
                                    className="flex-shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800"
                                    title="Cor fora do cadastro de cores e do de-para"
                                >
                                    Fora do padrão
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function AvailableOrdersPanel({
    state,
    shipmentId,
    onRefresh,
    usedByOop,
    itemsByOrder,
    onToast,
    disabled,
}: {
    state: POState;
    shipmentId: string;
    onRefresh: (next: POState) => void;
    usedByOop: Map<number, number>;
    itemsByOrder: Map<number, number>;
    onToast: (t: { kind: 'success' | 'error'; msg: string } | null) => void;
    disabled: boolean;
}) {
    const { available_orders, items, color_hex_maps, po } = state;
    const [zoomImage, setZoomImage] = useState<{
        url: string;
        name: string;
        code: string | null;
        colors: SlotColors;
    } | null>(null);
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<OrderSearchResult[]>([]);
    const [searched, setSearched] = useState(false);
    const [productFilter, setProductFilter] = useState('');
    const [hideInPo, setHideInPo] = useState(false);
    const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
    const listRef = useRef<HTMLDivElement>(null);

    useStaggerIn(listRef, available_orders.map((o) => o.ploomes_order_id).join(','));

    const filterText = productFilter.trim().toLowerCase();
    const visibleProducts = (order: AvailableOrder) =>
        order.products.filter((p) => {
            if (hideInPo) {
                const used = usedByOop.get(p.ploomes_order_product_id) ?? 0;
                if (p.quantity_total - used <= 0) return false;
            }
            if (!filterText) return true;
            return (
                p.product_name.toLowerCase().includes(filterText) ||
                (p.product_code ?? '').toLowerCase().includes(filterText)
            );
        });
    const filtering = filterText !== '' || hideInPo;
    const totalProducts = available_orders.reduce((acc, o) => acc + o.products.length, 0);
    const shownProducts = available_orders.reduce((acc, o) => acc + visibleProducts(o).length, 0);

    const toggleCollapsed = (orderId: number) =>
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });

    const flashRow = (oopId: number) =>
        requestAnimationFrame(() =>
            flash(listRef.current?.querySelector<HTMLElement>(`[data-oop="${oopId}"]`) ?? null),
        );

    useEffect(() => {
        if (!search.trim() || search.trim().length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/shipments/${shipmentId}/purchase-order/editor/order-search`, {
                    params: { q: search },
                });
                setResults(data?.results ?? data ?? []);
                setSearched(true);
            } catch {
                setResults([]);
                setSearched(true);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [search, shipmentId]);

    const addExtraMut = useMutation({
        mutationFn: (orderId: number) =>
            api
                .post(`/shipments/${shipmentId}/purchase-order/editor/extra-orders`, { ploomes_order_id: orderId })
                .then((r) => r.data),
        onSuccess: (d: any) => {
            onRefresh(d.state ?? d);
            setSearch('');
            setResults([]);
            onToast({ kind: 'success', msg: 'Pedido vinculado.' });
        },
    });

    const addOrderMut = useMutation({
        mutationFn: (orderId: number) =>
            api
                .post(`/shipments/${shipmentId}/purchase-order/editor/orders`, { ploomes_order_id: orderId })
                .then((r) => r.data),
        onSuccess: (d: any) => {
            onRefresh(d.state ?? d);
            onToast({ kind: 'success', msg: `${d.added ?? ''} item(ns) adicionado(s).` });
        },
        onError: (e: any) => onToast({ kind: 'error', msg: e?.response?.data?.message ?? 'Erro.' }),
    });

    const addAllOrdersMut = useMutation({
        mutationFn: async () => {
            const pending = available_orders.filter((o) => {
                const totalAvail = o.products.reduce((acc, p) => {
                    const used = usedByOop.get(p.ploomes_order_product_id) ?? 0;
                    return acc + Math.max(0, p.quantity_total - used);
                }, 0);
                return totalAvail > 0;
            });
            let totalAdded = 0;
            let lastState: any = null;
            for (const order of pending) {
                const { data } = await api.post(
                    `/shipments/${shipmentId}/purchase-order/editor/orders`,
                    { ploomes_order_id: order.ploomes_order_id },
                );
                totalAdded += Number(data?.added ?? 0);
                lastState = data?.state ?? data;
            }
            return { totalAdded, count: pending.length, lastState };
        },
        onSuccess: (r) => {
            if (r.lastState) onRefresh(r.lastState);
            if (r.count === 0) {
                onToast({ kind: 'success', msg: 'Nenhum pedido com itens disponíveis.' });
            } else {
                onToast({
                    kind: 'success',
                    msg: `${r.count} pedido(s) adicionado(s) — ${r.totalAdded} item(ns).`,
                });
            }
        },
        onError: (e: any) => onToast({ kind: 'error', msg: e?.response?.data?.message ?? 'Erro.' }),
    });

    const removeOrderMut = useMutation({
        mutationFn: (orderId: number) =>
            api.delete(`/shipments/${shipmentId}/purchase-order/editor/orders/${orderId}`).then((r) => r.data),
        onSuccess: (d: any) => {
            onRefresh(d.state ?? d);
            onToast({ kind: 'success', msg: 'Pedido removido.' });
        },
    });

    // Adiciona todo o disponível do produto em uma única ação (paridade v1.addItem)
    const addItemMut = useMutation({
        mutationFn: async (product: AvailableProduct & { ploomes_order_id: number; add_qty: number }) => {
            if (product.add_qty <= 0) return null;
            const existing = items.find((i) => i.ploomes_order_product_id === product.ploomes_order_product_id);
            let merged: POItem[];
            if (existing) {
                merged = items.map((i) =>
                    i.ploomes_order_product_id === product.ploomes_order_product_id
                        ? { ...i, quantity: i.quantity + product.add_qty }
                        : i,
                );
            } else {
                merged = [
                    ...items,
                    {
                        ploomes_order_product_id: product.ploomes_order_product_id,
                        ploomes_order_id: product.ploomes_order_id,
                        product_id: product.product_id,
                        product_code: product.product_code,
                        product_name: product.product_name,
                        quantity: product.add_qty,
                        unit_price_usd: product.unit_price_usd,
                        discount_percent: product.discount_percent,
                        cbm_unit: product.cbm_unit,
                        frame_colour: product.frame_colour,
                        colour_upholstery: product.colour_upholstery,
                        stitches: product.stitches,
                        arm_colour: product.arm_colour,
                    },
                ];
            }
            const { data } = await api.put(`/shipments/${shipmentId}/purchase-order/editor/items`, { items: merged });
            return data;
        },
        onSuccess: (d: any, vars) => {
            if (!d) return;
            onRefresh(d);
            flashRow(vars.ploomes_order_product_id);
        },
    });

    return (
        <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                    <PiShoppingCartSimpleBold className="text-gray-500" />
                    Pedidos do embarque ({available_orders.length})
                </h2>
                {!disabled && available_orders.length > 1 && (
                    <button
                        type="button"
                        onClick={() => addAllOrdersMut.mutate()}
                        disabled={addAllOrdersMut.isPending || addOrderMut.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <PiPlusBold />
                        {addAllOrdersMut.isPending ? 'Adicionando…' : 'Adicionar todos os pedidos'}
                    </button>
                )}
            </div>

            {/* Busca extra */}
            {!disabled && (
                <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                    <label className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                        <PiMagnifyingGlassBold /> Vincular outro pedido Ploomes a esta PO
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Digite o número do pedido ou ID Ploomes (mín. 2 caracteres)…"
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-9 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <PiXBold />
                            </button>
                        )}
                    </div>

                    {results.length > 0 && (
                        <div className="mt-2 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                            {results.map((r) => {
                                const alreadyLinked = po.extra_order_ids.includes(r.id);
                                return (
                                <div key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50">
                                    <div className="flex size-8 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                                        <PiFileTextBold />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-medium text-gray-800">
                                            Pedido {r.order_number ?? '—'}
                                            <span className="ml-1 text-xs text-gray-400">#{r.id}</span>
                                            {alreadyLinked && (
                                                <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-700">Vinculado</span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-x-2 text-xs text-gray-500">
                                            {r.date && <span>{r.date}</span>}
                                            <span>{r.products_count} produto(s)</span>
                                            {r.deal_id && <span>Deal #{r.deal_id}</span>}
                                        </div>
                                    </div>
                                    {alreadyLinked ? (
                                        <button
                                            type="button"
                                            onClick={() => removeOrderMut.mutate(r.id)}
                                            disabled={removeOrderMut.isPending}
                                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 disabled:opacity-60"
                                        >
                                            <PiTrashBold className="size-3.5" /> Desvincular
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => addExtraMut.mutate(r.id)}
                                            disabled={addExtraMut.isPending}
                                            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-dark disabled:opacity-60"
                                        >
                                            <PiLinkBold className="size-3.5" /> Vincular
                                        </button>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    )}
                    {results.length === 0 && searched && search.trim().length >= 2 && (
                        <div className="mt-2 rounded-lg bg-gray-50 px-3 py-3 text-center text-xs text-gray-500">
                            Nenhum pedido encontrado para “{search}”.
                        </div>
                    )}
                </div>
            )}

            {available_orders.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
                    <div className="relative min-w-[14rem] flex-1">
                        <PiFunnelBold className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={productFilter}
                            onChange={(e) => setProductFilter(e.target.value)}
                            placeholder="Filtrar produtos por SKU ou nome…"
                            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-8 pr-8 text-sm focus:border-primary focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        {productFilter && (
                            <button
                                type="button"
                                onClick={() => setProductFilter('')}
                                aria-label="Limpar filtro"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <PiXBold />
                            </button>
                        )}
                    </div>
                    <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs text-gray-600">
                        <input
                            type="checkbox"
                            checked={hideInPo}
                            onChange={(e) => setHideInPo(e.target.checked)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        Ocultar itens já na PO
                    </label>
                    {filtering && (
                        <span className="text-xs tabular-nums text-gray-500">
                            {shownProducts} de {totalProducts} produtos
                        </span>
                    )}
                    <div className="ml-auto"><ColorLegend prefix="Cores (Estrutura · Estofado · Costura · Braço):" /></div>
                </div>
            )}

            {available_orders.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-sm">
                    <PiPackageBold className="mx-auto mb-2 size-12 opacity-30" />
                    <div className="text-sm">
                        {items.length > 0
                            ? 'Todos os itens disponíveis deste embarque já estão na PO. Use Remover num pedido (chips acima) ou vincule outro pedido.'
                            : 'Nenhum pedido Ploomes vinculado a este embarque.'}
                    </div>
                </div>
            ) : (
                <div ref={listRef} className="space-y-4">
                    {filtering && shownProducts === 0 && (
                        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                            Nenhum produto encontrado com esse filtro.
                        </div>
                    )}
                    {available_orders.map((order) => {
                        const products = visibleProducts(order);
                        if (filtering && products.length === 0) return null;
                        const isCollapsed = collapsed.has(order.ploomes_order_id) && !filterText;
                        // Coluna do contador só existe se ainda há algo a adicionar (senão sobra buraco à direita).
                        const hasPending = order.products.some(
                            (p) => p.quantity_total - (usedByOop.get(p.ploomes_order_product_id) ?? 0) > 0,
                        );
                        const orderTotalUsd = order.products.reduce(
                            (acc, p) => acc + p.quantity_available * p.unit_price_usd,
                            0,
                        );
                        const orderTotalCbm = order.products.reduce(
                            (acc, p) => acc + p.quantity_available * p.cbm_unit,
                            0,
                        );
                        const totalAvailable = order.products.reduce((acc, p) => acc + p.quantity_available, 0);
                        const alreadyAdded = itemsByOrder.get(order.ploomes_order_id) ?? 0;
                        const isExtra = po.extra_order_ids.includes(order.ploomes_order_id);
                        const canRemove = !disabled && (alreadyAdded > 0 || isExtra);
                        const canDesvincular = !disabled && isExtra;

                        return (
                            <div
                                key={order.ploomes_order_id}
                                id={`po-order-${order.ploomes_order_id}`}
                                data-motion-item
                                className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                            >
                                {/* Cabeçalho do pedido */}
                                <div
                                    className={
                                        'flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-4 py-3 ' +
                                        (isCollapsed ? '' : 'border-b border-gray-200')
                                    }
                                >
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => toggleCollapsed(order.ploomes_order_id)}
                                            aria-expanded={!isCollapsed}
                                            title={isCollapsed ? 'Expandir pedido' : 'Recolher pedido'}
                                            className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary transition hover:bg-primary/20"
                                        >
                                            <PiCaretDownBold
                                                className={'transition-transform duration-300 ' + (isCollapsed ? '-rotate-90' : '')}
                                            />
                                        </button>
                                        <div>
                                            <div className="font-semibold text-gray-800">
                                                Pedido {order.order_number ?? '—'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {order.order_date ?? ''} · {order.products.length} produto(s)
                                                {alreadyAdded > 0 && (
                                                    <>
                                                        {' · '}
                                                        <span className="font-medium text-emerald-600">
                                                            {alreadyAdded} SKUs na PO
                                                        </span>
                                                        {(() => {
                                                            const totalInPo = items
                                                                .filter((i) => i.ploomes_order_id === order.ploomes_order_id)
                                                                .reduce((s, i) => s + i.quantity, 0);
                                                            return totalInPo > 0 ? (
                                                                <span className="text-gray-400"> ({totalInPo} equip.)</span>
                                                            ) : null;
                                                        })()}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {totalAvailable > 0 ? (
                                            <div className="text-right">
                                                <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                                    Falta adicionar
                                                </div>
                                                <div className="text-sm font-semibold tabular-nums text-gray-800">
                                                    {fmtUSD(orderTotalUsd)}
                                                </div>
                                                <div className="text-xs tabular-nums text-gray-500">{fmtCBM(orderTotalCbm)} m³</div>
                                            </div>
                                        ) : null}
                                        {totalAvailable > 0 ? (
                                            <button
                                                type="button"
                                                onClick={() => addOrderMut.mutate(order.ploomes_order_id)}
                                                disabled={disabled || addOrderMut.isPending}
                                                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-40"
                                            >
                                                <PiPlusBold /> Adicionar pedido
                                            </button>
                                        ) : alreadyAdded > 0 ? (
                                            <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-100 px-3 py-2 text-xs font-semibold text-emerald-700">
                                                <PiCheckBold /> Tudo na PO
                                            </span>
                                        ) : null}
                                        {canRemove && (
                                            <button
                                                type="button"
                                                onClick={() => removeOrderMut.mutate(order.ploomes_order_id)}
                                                disabled={removeOrderMut.isPending}
                                                title={
                                                    isExtra
                                                        ? 'Remover itens e desvincular pedido'
                                                        : 'Remover todos os itens deste pedido da PO'
                                                }
                                                className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-60"
                                            >
                                                <PiTrashBold /> {canDesvincular ? 'Desvincular' : 'Remover'}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Linhas de produto */}
                                {!isCollapsed && (
                                <div className="divide-y divide-gray-100">
                                    {products.map((p) => {
                                        const used = usedByOop.get(p.ploomes_order_product_id) ?? 0;
                                        const total = p.quantity_total;
                                        const available = Math.max(0, total - used);
                                        const exhausted = available <= 0;
                                        return (
                                            <div
                                                key={p.ploomes_order_product_id}
                                                data-oop={p.ploomes_order_product_id}
                                                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50"
                                            >
                                                {p.image_url ? (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setZoomImage({ url: p.image_url!, name: p.product_name, code: p.product_code, colors: p })
                                                        }
                                                        className="group flex-shrink-0 cursor-zoom-in rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                                        title="Ampliar imagem"
                                                        aria-label={`Ampliar imagem de ${p.product_name}`}
                                                    >
                                                        <img
                                                            src={p.image_url}
                                                            alt=""
                                                            loading="lazy"
                                                            className="size-12 rounded-lg object-cover ring-1 ring-gray-200 transition duration-200 group-hover:scale-105 group-hover:ring-primary/50"
                                                        />
                                                    </button>
                                                ) : (
                                                    <div className="flex size-12 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                                                        <PiCubeBold className="size-5 text-gray-400" />
                                                    </div>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate font-medium text-gray-800">
                                                        {p.product_name}
                                                    </div>
                                                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                                                        {p.product_code && (
                                                            <span>
                                                                <strong>{p.product_code}</strong>
                                                            </span>
                                                        )}
                                                        <span>{fmtUSD(p.unit_price_usd)}/un</span>
                                                        {p.discount_percent > 0 && (
                                                            <span
                                                                className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600"
                                                                title="Desconto dado ao cliente no Ploomes — só negociação Brasil (BRL), não entra na PO para a China"
                                                            >
                                                                Desc. Ploomes {fmtPct(p.discount_percent)}
                                                            </span>
                                                        )}
                                                        <span>{fmtCBM(p.cbm_unit, 4)} m³/un</span>
                                                        {p.is_high_turnover && (
                                                            <span
                                                                className="rounded bg-sky-100 px-1.5 py-0.5 text-[11px] font-semibold text-sky-800"
                                                                title="Alto giro: confirme com o comercial se entra como estoque ou pronta-entrega"
                                                            >
                                                                Alto giro
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="w-24 flex-shrink-0">
                                                    <div className="grid grid-cols-4 gap-1">
                                                        {COLOR_SLOTS.map(
                                                            (slot) => {
                                                                const name = slotColor(p, slot);
                                                                return (
                                                                    <div
                                                                        key={slot}
                                                                        className="flex items-center justify-center"
                                                                    >
                                                                        <ColorDot slot={slot} name={name} hexMaps={color_hex_maps} />
                                                                    </div>
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                </div>
                                                <QtyStatus used={used} total={total} available={available} />
                                                {hasPending && (
                                                <div className="flex w-[11.5rem] flex-shrink-0 justify-end">
                                                {!exhausted && (
                                                <ProductStepper
                                                    product={p}
                                                    orderId={order.ploomes_order_id}
                                                    used={used}
                                                    available={available}
                                                    total={total}
                                                    disabled={disabled || addItemMut.isPending}
                                                    onAdd={(qty) =>
                                                        addItemMut.mutate({
                                                            ...p,
                                                            ploomes_order_id: order.ploomes_order_id,
                                                            add_qty: qty,
                                                        })
                                                    }
                                                />
                                                )}
                                                </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Pedidos órfãos: itens na PO cujo deal não está mais vinculado ao shipment */}
            {state.orphaned_orders && state.orphaned_orders.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
                        <PiWarningBold className="size-4 shrink-0" />
                        Pedidos órfãos — itens na PO sem deal vinculado ao embarque
                    </div>
                    <p className="mb-3 text-xs text-amber-700">
                        Estes pedidos têm itens na PO, mas o deal deles não está ligado a este embarque
                        (nem como extra). Se o pedido deveria entrar nesta PO, vincule o deal ao embarque;
                        caso contrário, remova os itens.
                    </p>
                    <div className="space-y-2">
                        {state.orphaned_orders.map((o) => (
                            <div
                                key={o.ploomes_order_id}
                                className="flex items-center justify-between rounded-lg border border-amber-200 bg-white px-4 py-3"
                            >
                                <div>
                                    <span className="font-medium text-gray-800">
                                        Pedido {o.order_number ?? `#${o.ploomes_order_id}`}
                                    </span>
                                    {o.order_date && (
                                        <span className="ml-2 text-xs text-gray-500">{o.order_date}</span>
                                    )}
                                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                                        {o.items_count} item(ns) na PO
                                    </span>
                                </div>
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (confirm(`Remover todos os itens do pedido ${o.order_number ?? o.ploomes_order_id} desta PO?`)) {
                                                removeOrderMut.mutate(o.ploomes_order_id);
                                            }
                                        }}
                                        disabled={removeOrderMut.isPending}
                                        className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
                                    >
                                        <PiTrashBold /> Remover
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <V2Modal
                open={zoomImage !== null}
                onClose={() => setZoomImage(null)}
                title={zoomImage?.name ?? ''}
                description={zoomImage?.code ?? undefined}
                size="5xl"
            >
                {zoomImage && (
                    <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_17rem]">
                        <div className="flex items-center justify-center rounded-xl bg-gray-50 p-4 dark:bg-gray-800/60">
                            <img
                                src={zoomImage.url}
                                alt={zoomImage.name}
                                onLoad={(e) => fadeZoomIn(e.currentTarget)}
                                className="max-h-[60vh] w-auto object-contain"
                            />
                        </div>
                        <ProductColorsPanel colors={zoomImage.colors} hexMaps={color_hex_maps} />
                    </div>
                )}
            </V2Modal>
        </section>
    );
}

/* ============================== CONTAINERS GRID ============================== */

function ContainersCard({ totals }: { totals: POState['totals'] }) {
    const max = totals.max_containers ?? null;
    const used = totals.containers;
    const cbmPer = totals.cbm_per_container;
    const renderSlots = Math.max(used, max ?? 0, 1);
    const fullUsed = Math.floor(totals.cbm / cbmPer);
    const hasPartial = totals.cbm - fullUsed * cbmPer > 0.0001;
    const exceeded = max !== null && used > max;
    const capacity = max !== null ? max * cbmPer : null;
    const excessCbm = capacity !== null ? Math.max(0, totals.cbm - capacity) : 0;
    const gridRef = useRef<HTMLDivElement>(null);
    const usedRef = useRef<HTMLSpanElement>(null);
    const prevUsed = useRef(used);

    useStaggerIn(gridRef, 'containers');

    useEffect(() => {
        if (prevUsed.current !== used) pop(usedRef.current);
        prevUsed.current = used;
    }, [used]);

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Containers ({cbmPer} m³{max ? ` · máx ${max}` : ''})
            </h3>
            <div ref={gridRef} className="grid grid-cols-5 gap-2">
                {Array.from({ length: renderSlots }).map((_, i) => {
                    const n = i + 1;
                    const isFull = n <= fullUsed;
                    const isPartial = !isFull && n === fullUsed + 1 && hasPartial;
                    const isExceed = exceeded && max !== null && n === max;
                    const slotCbm = Math.min(cbmPer, Math.max(0, totals.cbm - i * cbmPer));
                    return (
                        <ContainerSlot
                            key={i}
                            n={n}
                            slotCbm={slotCbm}
                            cbmPer={cbmPer}
                            tone={isExceed ? 'exceed' : isFull ? 'full' : isPartial ? 'partial' : 'empty'}
                        />
                    );
                })}
            </div>
            <div className="mt-3 text-center text-sm">
                <span className="text-gray-500">Usados: </span>
                <span
                    ref={usedRef}
                    className={
                        'inline-block text-base font-bold ' +
                        (exceeded
                            ? 'text-red-600'
                            : used > 0
                              ? 'text-emerald-600'
                              : 'text-gray-400')
                    }
                >
                    {used}
                </span>
                <span className="text-gray-400">{max ? ` / ${max}` : ''}</span>
            </div>
            {exceeded && (
                <div className="mt-3 space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                    <div className="flex items-start gap-2">
                        <span className="text-base leading-none">⚠</span>
                        <div className="flex-1 space-y-0.5">
                            <div className="font-semibold text-red-700">
                                Limite excedido em {fmtCBM(excessCbm)} m³
                            </div>
                            <div className="text-red-600/90">
                                Total {fmtCBM(totals.cbm)} m³ · capacidade {capacity !== null ? fmtCBM(capacity) : '—'} m³
                                ({max} × {cbmPer} m³)
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

/* ============================== SUPPLIER & IMPORTER ============================== */

function SupplierCard({ detail }: { detail: SupplierDetail | null }) {
    return (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-start justify-between gap-2">
                <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <PiTruckBold className="size-4" /> Fornecedor
                </h3>
                {detail && (
                    <a
                        href={detail.manage_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-primary hover:underline"
                    >
                        Gerenciar
                    </a>
                )}
            </div>
            {!detail ? (
                <div className="text-sm text-gray-500">Nenhum fornecedor selecionado. Edite o setup para escolher.</div>
            ) : (
                <div className="space-y-1 text-sm">
                    <div className="text-base font-bold text-gray-800">{detail.name}</div>
                    {detail.legal_name && <div className="text-xs text-gray-500">{detail.legal_name}</div>}
                    {detail.attn && (
                        <div className="text-xs">
                            <span className="text-gray-500">ATTN:</span> {detail.attn}
                        </div>
                    )}
                    {detail.address && (
                        <div className="whitespace-pre-line text-xs text-gray-600">{detail.address}</div>
                    )}
                    {detail.phone && (
                        <div className="text-xs">
                            <span className="text-gray-500">Tel:</span> {detail.phone}
                        </div>
                    )}
                    {detail.email && (
                        <div className="truncate text-xs">
                            <span className="text-gray-500">E-mail:</span>{' '}
                            <a href={`mailto:${detail.email}`} className="text-primary hover:underline">
                                {detail.email}
                            </a>
                        </div>
                    )}
                    {detail.max_containers ? (
                        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
                            Limite: {detail.max_containers} containers
                        </div>
                    ) : null}
                </div>
            )}
        </section>
    );
}

function ImporterCard({ detail }: { detail: BranchDetail | null }) {
    return (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-start justify-between gap-2">
                <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <PiBuildingOfficeBold className="size-4" /> Importador (Filial Buckler)
                </h3>
                {detail && (
                    <a
                        href={detail.manage_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-primary hover:underline"
                    >
                        Gerenciar
                    </a>
                )}
            </div>
            {!detail ? (
                <div className="text-sm text-gray-500">Nenhuma filial selecionada. Edite o setup para escolher.</div>
            ) : (
                <div className="space-y-1 text-sm">
                    <div className="font-bold text-gray-800">{detail.name}</div>
                    {detail.legal_name && <div className="text-xs">{detail.legal_name}</div>}
                    {detail.cnpj && (
                        <div className="text-xs">
                            <span className="text-gray-500">CNPJ:</span> {detail.cnpj}
                        </div>
                    )}
                    {detail.state_registration && (
                        <div className="text-xs">
                            <span className="text-gray-500">IE:</span> {detail.state_registration}
                        </div>
                    )}
                    {detail.full_address && (
                        <div className="mt-1 whitespace-pre-line text-xs text-gray-600">{detail.full_address}</div>
                    )}
                    {detail.contact_person && (
                        <div className="text-xs">
                            <span className="text-gray-500">Contato:</span> {detail.contact_person}
                        </div>
                    )}
                    {detail.phone && (
                        <div className="text-xs">
                            <span className="text-gray-500">Tel:</span> {detail.phone}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

/* ============================== NOTES ============================== */

function NotesCard({
    state,
    shipmentId,
    onRefresh,
    disabled,
}: {
    state: POState;
    shipmentId: string;
    onRefresh: (next: POState) => void;
    disabled: boolean;
}) {
    const [notes, setNotes] = useState(state.po.notes ?? '');
    useEffect(() => setNotes(state.po.notes ?? ''), [state.po.notes]);

    const saveMut = useMutation({
        mutationFn: () =>
            api
                .put(`/shipments/${shipmentId}/purchase-order/editor/meta`, {
                    notes,
                    color: state.po.color,
                })
                .then((r) => r.data),
        onSuccess: (d: any) => onRefresh(d.state ?? d),
    });

    // debounce automático (paridade Filament: wire:model.live.debounce.700ms)
    useEffect(() => {
        if (disabled) return;
        if (notes === (state.po.notes ?? '')) return;
        const t = setTimeout(() => saveMut.mutate(), 700);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notes]);

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Observações</h3>
            <textarea
                disabled={disabled}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Notas para o fornecedor..."
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
            />
        </section>
    );
}

/* ============================== PDF GENERATED ============================== */

function PdfGeneratedCard({ pdfUrl, closedAt }: { pdfUrl: string; closedAt: string | null }) {
    const dt = closedAt ? new Date(closedAt) : null;
    const label = dt
        ? `${dt.toLocaleDateString('pt-BR')} ${dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
        : '';
    return (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-emerald-600 text-white">
                    <PiCheckCircleBold className="size-6" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-emerald-800">PDF gerado</div>
                    {label && <div className="text-xs text-emerald-700">{label}</div>}
                </div>
                <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                    Abrir
                </a>
            </div>
        </div>
    );
}
